// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { EventEmitter } from 'events';
import { ThemeManager } from '@front/controls/misc/ThemeManager';
import { DataCacheToolRayType } from '@shared/commons/cache/DataCacheToolEnums';
import { Cursors } from '@shared/commons/Cursors';
import { Point } from '@shared/commons/Geometry';
import { SolidBrush } from '@shared/commons/Graphics';
import { Selection, SelectionState } from './Selection';
import { ChartMath } from '../Utils/ChartMath';
export class ToolView {
    constructor(dataCacheTool, chartId) {
        this.pointsLabel = {}; // Object: { '<screenPointIndex>': textNearPoint }
        this.movingType = MovingType.All;
        this.excludedOuterActionTypes = [];
        this.pointRadius = 8;
        this._eventEmitter = null;
        this._eventEmitter = new EventEmitter();
        this.chartViewId = chartId;
        this.dataCacheTool = dataCacheTool;
        this.CurrentSelection = this.CreateSelection();
        this.CurrentMovement = this.CreateMovement();
        this.InitPoints(dataCacheTool.PointLevel() + dataCacheTool.AdditionalPointsCount());
        this.Localize();
    }
    CreateSelection() {
        return new Selection();
    }
    CreateMovement() {
        return new Movement();
    }
    InitPoints(level) {
        this.screenPoints = Array(level);
        for (let i = 0; i < this.screenPoints.length; i++) {
            this.screenPoints[i] = Array(2);
        }
    }
    /// <summary>
    ///
    /// </summary>
    Draw(gr, ww, param) {
        // Ловим ошибки
        if (this.screenPoints[0][0] >= ProMath.infinity ||
            this.screenPoints[0][1] >= ProMath.infinity ||
            this.screenPoints[0][0] <= ProMath.infinityMinus ||
            this.screenPoints[0][1] <= ProMath.infinityMinus) {
            return;
        }
        if (this.CurrentSelection.SelectedPointNo > -1) {
            // ++ все точки подсвечиваем
            for (let i = 0; i < this.dataCacheTool.PointLevel(); i++) {
                this.DrawSelectedPoint(gr, ww, this.screenPoints[i][0], this.screenPoints[i][1], i);
            }
        }
        else if (this.CurrentSelection.CurrentState !== SelectionState.None) {
            this.DrawSelection(gr, ww, param);
        }
    }
    DrawSelectedPoint(gr, ww, x, y, pointIndex) {
        if (x >= ProMath.infinity ||
            y >= ProMath.infinity ||
            x <= ProMath.infinityMinus ||
            y <= ProMath.infinityMinus) {
            return;
        }
        let brush;
        if (this.CurrentMovement.MovingPointNo === pointIndex) {
            brush = new SolidBrush(ThemeManager.colors.chartToolsMovePointBackground);
        }
        else {
            brush = new SolidBrush(ThemeManager.colors.chartToolsPointsFill);
        }
        const r = this.pointRadius;
        gr.FillEllipse(brush, x - r / 2, y - r / 2, r, r, this.dataCacheTool.penHighlight);
    }
    DrawSelectedLine(gr, ww, x1, y1, x2, y2) {
        gr.save();
        gr.DrawLine(this.dataCacheTool.PenHighlight, x1, y1, x2, y2);
        gr.DrawLine(this.dataCacheTool.PenHalo, x1, y1, x2, y2);
        gr.restore();
    }
    DrawSelectedLines(gr, ww, param) {
        for (let firstPointIdx = 0; firstPointIdx < this.dataCacheTool.PointLevel(); firstPointIdx++) {
            const secondPointIdx = firstPointIdx + 1 < this.dataCacheTool.PointLevel() ? firstPointIdx + 1 : 0;
            if (!param || param.isNeedConnectFirstAndLastPoints || secondPointIdx > 0) {
                this.DrawSelectedLine(gr, ww, this.screenPoints[firstPointIdx][0], this.screenPoints[firstPointIdx][1], this.screenPoints[secondPointIdx][0], this.screenPoints[secondPointIdx][1]);
            }
        }
    }
    /// <summary>
    /// Отрисовка выделения тулзы
    /// </summary>
    DrawSelection(gr, ww, param) {
        this.DrawSelectedLines(gr, ww, param);
        for (let i = 0; i < this.dataCacheTool.PointLevel(); i++) {
            this.DrawSelectedPoint(gr, ww, this.screenPoints[i][0], this.screenPoints[i][1], i);
        }
    }
    CopyScreenPoints() {
        const screenPoints = this.screenPoints;
        const len = screenPoints.length;
        const copy = Array(len);
        for (let i = 0; i < len; i++) {
            copy[i] = screenPoints[i].slice();
        }
        return copy;
    }
    /// <summary>
    /// Рассчитать экранные координаты
    /// </summary>
    UpdateScreenPoints(ww, cashItemSeries = null) {
        this.UpdateScreenPointsALL(ww, cashItemSeries, true);
        //
        // callback need recalculate points
        //
        if (this.dataCacheTool.forceUpdateAdditionalPoints) {
            this.dataCacheTool.forceUpdateAdditionalPoints = false;
            this.UpdateAdditionalPoints(ww, -1, cashItemSeries);
            this.UpdateScreenPointsALL(ww, cashItemSeries, false);
        }
    }
    UpdateScreenPointsALL(ww, cashItemSeries = null, onlyPointLevel = false) {
        const points = onlyPointLevel ? this.dataCacheTool.PointLevel() : this.dataCacheTool.Points.length;
        const temp = this.CopyScreenPoints();
        for (let i = 0; i < points; i++) {
            temp[i][0] = Math.round(ww.PointsConverter.GetScreenXbyTime(this.dataCacheTool.Points[i][0]));
            temp[i][1] =
                ww.IsMainWindow && (cashItemSeries === null || cashItemSeries === void 0 ? void 0 : cashItemSeries.settings)
                    ? Math.round(ww.PointsConverter.GetScreenY(cashItemSeries.settings.CalculateConverter(this.dataCacheTool.Points[i][1])))
                    : Math.round(ww.PointsConverter.GetScreenY(this.dataCacheTool.Points[i][1]));
        }
        this.screenPoints = temp;
    }
    OnFinishCreationTool(e) {
        // to be overriden if necessary
    }
    /// <summary>
    /// !!
    /// </summary>
    UpdateAdditionalPoints(ww, pointNo, cashItemSeries = null) {
        // to be overriden if necessary
    }
    // #region Drawing Utils
    static DrawLineWithRay(gr, ww, xx0, yy0, xx1, yy1, ray, linePen, rayPen = null) {
        gr.DrawLine(linePen, xx0, yy0, xx1, yy1);
        if (ray !== DataCacheToolRayType.NoRay) {
            const clientRect = ww.ClientRectangle;
            // из уравнения прямой через 2 точки находим где луч пересечет границы экрана
            const points = [];
            const screen_intersect_by_ray_yy2 = ((clientRect.X - xx0) * (yy1 - yy0)) / (xx1 - xx0) + yy0;
            if (screen_intersect_by_ray_yy2 > clientRect.Y && screen_intersect_by_ray_yy2 < clientRect.Height) {
                points.push(new Point(clientRect.X, screen_intersect_by_ray_yy2));
            }
            const screen_intersect_by_ray_yy3 = ((clientRect.Width - xx0) * (yy1 - yy0)) / (xx1 - xx0) + yy0;
            if (screen_intersect_by_ray_yy3 > clientRect.Y && screen_intersect_by_ray_yy3 < clientRect.Height) {
                points.push(new Point(clientRect.Width, screen_intersect_by_ray_yy3));
            }
            const screen_intersect_by_ray_xx2 = ((clientRect.Y - yy0) * (xx1 - xx0)) / (yy1 - yy0) + xx0;
            if (screen_intersect_by_ray_xx2 > clientRect.X && screen_intersect_by_ray_xx2 < clientRect.Width) {
                points.push(new Point(screen_intersect_by_ray_xx2, clientRect.Y));
            }
            const screen_intersect_by_ray_xx3 = ((clientRect.Height - yy0) * (xx1 - xx0)) / (yy1 - yy0) + xx0;
            if (screen_intersect_by_ray_xx3 > clientRect.X && screen_intersect_by_ray_xx3 < clientRect.Width) {
                points.push(new Point(screen_intersect_by_ray_xx3, clientRect.Height));
            }
            // если есть пересечение лучем экрана
            if (points.length == 2) {
                let xx2 = 0;
                let xx3 = 0;
                let yy2 = 0;
                let yy3 = 0; // продолжение луча
                if (ray === DataCacheToolRayType.DoubleRay) {
                    // точка с которой луч
                    xx2 = points[0].X;
                    yy2 = points[0].Y;
                    // точка в которую
                    xx3 = points[1].X;
                    yy3 = points[1].Y;
                }
                if (ray === DataCacheToolRayType.RightRay || (ray === DataCacheToolRayType.Ray && xx0 < xx1)) {
                    // точка с которой луч
                    xx2 = xx0 > xx1 ? xx0 : xx1;
                    yy2 = xx0 > xx1 ? yy0 : yy1;
                    // точка в которую
                    xx3 = points[0].X > points[1].X ? points[0].X : points[1].X;
                    yy3 = points[0].X > points[1].X ? points[0].Y : points[1].Y;
                }
                if (ray === DataCacheToolRayType.LeftRay || (ray === DataCacheToolRayType.Ray && xx0 > xx1)) {
                    // точка с которой луч
                    xx2 = xx0 < xx1 ? xx0 : xx1;
                    yy2 = xx0 < xx1 ? yy0 : yy1;
                    // точка в которую
                    xx3 = points[0].X < points[1].X ? points[0].X : points[1].X;
                    yy3 = points[0].X < points[1].X ? points[0].Y : points[1].Y;
                }
                if (ray === DataCacheToolRayType.Ray && xx0 === xx1) // #51411 - частный случай
                 {
                    xx2 = xx0;
                    yy2 = yy0 > yy1 ? yy1 : yy0;
                    xx3 = points[0].X;
                    yy3 = yy0 > yy1 ? points[0].Y : points[1].Y;
                }
                gr.DrawLine(rayPen || linePen, xx2, yy2, xx3, yy3);
            }
        }
    }
    static DrawTextByAngle(gr, text, font, brush, x0, y0, x1, y1, rotation) {
        gr.save();
        gr.translate(x0, y0);
        gr.rotate((rotation * Math.PI) / 180);
        gr.translate(-x0, -y0);
        const width = gr.GetTextWidth(text, font);
        gr.DrawString(text, font, brush, x1 - width, y1 - font.Height - 6);
        gr.restore();
    }
    ConvertPrice(price, cashItemSeriesSettings) {
        if (cashItemSeriesSettings) {
            return cashItemSeriesSettings.CalculateConverter(price);
        }
        else {
            return price;
        }
    }
    ConvertPoints(x, y, pc, cashItemSeriesSettings) {
        if (cashItemSeriesSettings) {
            return [pc.GetDataX(x), cashItemSeriesSettings.RevertConverter(pc.GetDataY(y))];
        }
        else {
            return [pc.GetDataX(x), pc.GetDataY(y)];
        }
    }
    // #endregion
    // #region Theme Changed, Localize
    Localize() {
        this.dataCacheTool.Localize();
    }
    // #endregion
    // #region ICaller
    Properties() {
        return this.dataCacheTool.Properties();
    }
    callBack(properties) {
        const dc = this.dataCacheTool;
        dc.callBack(properties);
        dc.forceUpdateAdditionalPoints = true;
        this.FireUpdate();
    }
    // #endregion
    Dispose() {
        this._eventEmitter.emit('callerDisposed');
        this.dataCacheTool = null;
        this.isDisposed = true;
    }
    /// <summary>
    /// сообщение о необходимости перерисовки
    /// </summary>
    FireUpdate() {
        this.dataCacheTool.FireUpdate();
        // console.log("tool FireUpdate");
    }
    /// <summary>
    /// Является ли тулза выделенной мышью (сейчас по линиям, но можно и весь полигон)
    /// </summary>
    IsSelectCheck(x, y) {
        for (let i = 1; i < this.dataCacheTool.PointLevel(); i++) {
            if (ChartMath.CalcDistanceSqrFromPointToSection(this.screenPoints[i - 1], this.screenPoints[i], x, y) <=
                ToolView.TOOL_DX) {
                return true;
            }
        }
        return false;
    }
    SelectTool() {
        this.CurrentSelection.CurrentState = SelectionState.Selected;
    }
    ResetSelection() {
        this.CurrentSelection.CurrentState = SelectionState.None;
    }
    IsSelect(x, y) {
        const selected = this.PreSelectAction(x, y) || this.IsSelectCheck(x, y);
        this.PostSelectAction(x, y, selected);
        return selected;
    }
    /// <summary>
    /// Предварительные проверки - если вдруг тулзы скрыта и т.п. вернуть false
    /// </summary>
    PreSelectAction(x, y) {
        this.CurrentSelection.SelectedPointNo = this.FindSelectedPoint(x, y);
        if (this.CurrentSelection.CurrentState === SelectionState.Selected) // выделение запомнено
         {
            return true;
        }
        if (this.CurrentMovement.IsMovingRightNow) {
            return true;
        }
        if (this.CurrentSelection.SelectedPointNo >= 0) // выделена точка, значит и вся тулза
         {
            return true;
        }
        return null;
    }
    PostSelectAction(x, y, isSelect) {
        switch (this.CurrentSelection.CurrentState) {
            case SelectionState.None:
                if (isSelect) {
                    this.CurrentSelection.CurrentState = SelectionState.Hovered;
                    this.FireUpdate();
                }
                break;
            case SelectionState.Hovered:
                if (!isSelect) {
                    this.CurrentSelection.CurrentState = SelectionState.None;
                    this.FireUpdate();
                }
                break;
        }
        this.CurrentMovement.CreationState = false;
    }
    FindSelectedPoint(x, y) {
        for (let i = 0; i < this.dataCacheTool.PointLevel(); i++) {
            const p = this.screenPoints[i];
            if (p[0] - x < ToolView.POINT_DX &&
                p[0] - x > -ToolView.POINT_DX &&
                p[1] - y < ToolView.POINT_DX &&
                p[1] - y > -ToolView.POINT_DX) {
                return i;
            }
        }
        return -1;
    }
    // #endregion
    // #region Movement
    MinDistanceForStartMoving() {
        return 1;
    }
    CheckMinDistanceForStartMoving(x, y) {
        return (Math.abs(this.CurrentMovement.PrevX - x) + Math.abs(this.CurrentMovement.PrevY - y) >=
            this.MinDistanceForStartMoving());
    }
    /// <summary>
    /// тулза выделяется (активируется). будет ли перемещение потом - неизвестно
    /// </summary>
    ProcessActivate(x, y) {
        const selectedPointNo = this.FindSelectedPoint(x, y);
        if (selectedPointNo > -1) {
            this.CurrentMovement.MovingPointNo = selectedPointNo;
            return true;
        }
        if (this.IsSelectCheck(x, y)) {
            this.CurrentMovement.PrevX = x;
            this.CurrentMovement.PrevY = y;
            this.CurrentMovement.PrevScreenPoints = null;
            this.CurrentMovement.WholeTool = true;
            return true;
        }
        return false;
    }
    /// <summary>
    /// началось тягание тулзы
    /// </summary>
    ProcessMoveStart(x, y) {
        this.CurrentMovement.IsMovingRightNow = true;
    }
    /// <summary>
    /// новое положение тулзы
    /// </summary>
    ProcessNewPosition(window, x, y, cashItemSeries = null) {
        if (window == null || this.dataCacheTool == null) {
            return;
        }
        const movingPointNo = this.CurrentMovement.MovingPointNo;
        if (movingPointNo > -1) {
            this.MoveScreenPoints(window, movingPointNo, x, y, cashItemSeries);
        }
        else if (this.CurrentMovement.WholeTool) {
            // ! нельзя двигать как инт и преобразовать потом в дабл, перевел смещение тулз на даблы
            if (this.CurrentMovement.PrevScreenPoints == null) {
                // точки смоздаем только если начали смещать
                const temp = this.dataCacheTool.CopyPoints();
                for (let i = 0; i < temp.length; i++) {
                    temp[i][0] = window.PointsConverter.GetScreenXbyTime(this.dataCacheTool.Points[i][0]);
                    temp[i][1] = window.PointsConverter.GetScreenY(cashItemSeries.settings.CalculateConverter(this.dataCacheTool.Points[i][1]));
                }
                this.CurrentMovement.PrevScreenPoints = temp;
                this.CurrentMovement.WholeTool = true;
            }
            const dx = x - this.CurrentMovement.PrevX;
            const dy = y - this.CurrentMovement.PrevY;
            const prevPosition = this.CurrentMovement.PrevScreenPoints;
            for (let i = 0; i < prevPosition.length; i++) {
                const point = prevPosition[i];
                this.MoveScreenPoints(window, i, point[0] + dx, point[1] + dy, cashItemSeries, false, false);
            }
        }
        this.FireUpdate();
    }
    /// <summary>
    /// Изменение координат точки №pointNo
    /// </summary>
    MoveScreenPoints(ww, pointNo, newX, newY, cashItemSeries = null, callFireUpdate = true, callUpdateAdditionalPoints = true) {
        if (pointNo < 0 || pointNo >= this.dataCacheTool.Points.length) {
            return;
        }
        const point = this.dataCacheTool.Points[pointNo];
        if (this.movingType !== MovingType.OnlyY) {
            point[0] = ww.PointsConverter.GetDataX(newX);
        }
        const newValue = ww.IsMainWindow && cashItemSeries
            ? cashItemSeries.settings.RevertConverter(ww.PointsConverter.GetDataY(newY))
            : ww.PointsConverter.GetDataY(newY);
        point[1] = newValue;
        if (callUpdateAdditionalPoints) {
            this.UpdateAdditionalPoints(ww, pointNo, cashItemSeries);
        }
        if (callFireUpdate) {
            this.FireUpdate();
        }
    }
    /// <summary>
    /// тягание тулзы завершено (вызывается после ProcessMoveStart)
    /// </summary>
    ProcessMoveFinish() {
        this.CurrentMovement.IsMovingRightNow = false;
    }
    /// <summary>
    /// выделние тулзы завершено (неважно, было ли перемещение)
    /// </summary>
    ProcessDeactivate() {
        this.CurrentMovement.MovingPointNo = -1;
        this.CurrentMovement.WholeTool = false;
        this.CurrentMovement.PrevScreenPoints = null; // необязательно
    }
    ProcessClick(e) { }
    ProcessDoubleClick(e) { }
    // #endregion
    // #region MouseActions
    OnMouseEnter(e) { }
    OnMouseMove(e) { }
    OnMouseLeave(e) { }
    GetCursor(e) {
        if (this.CurrentSelection.SelectedPointNo > -1) {
            return Cursors.Hand;
        }
        return Cursors.Default;
    }
    GetPointLabel(pointIndex) {
        const result = this.pointsLabel[pointIndex] || '';
        return result;
    }
    AddPointLabel(pointIndex, labelText) {
        // returns old label text value if change with new text
        const oldLabel = this.pointsLabel[pointIndex] || null;
        this.pointsLabel[pointIndex] = labelText;
        return oldLabel != labelText ? oldLabel : null;
    }
    applyProperties(properties) {
        var _a;
        (_a = this.dataCacheTool) === null || _a === void 0 ? void 0 : _a.callBack(properties);
        this.dataCacheTool.forceUpdateAdditionalPoints = true;
        this.FireUpdate();
    }
    subscribeOnCallerClosed(callback) {
        this._eventEmitter.on('callerDisposed', callback);
    }
    unsubscribeOnCallerClosed(callback) {
        this._eventEmitter.off('callerDisposed', callback);
    }
}
// #region Selection
/// <summary>
/// допуск при выделении точки
/// </summary>
ToolView.POINT_DX = 4;
/// <summary>
/// допуск при выделении линии
/// </summary>
ToolView.TOOL_DX = 16; // рекомендовано POINT_DX^2
// #endregion
// #region Utils
export var MovingType;
(function (MovingType) {
    MovingType[MovingType["All"] = 0] = "All";
    MovingType[MovingType["OnlyX"] = 1] = "OnlyX";
    MovingType[MovingType["OnlyY"] = 2] = "OnlyY";
})(MovingType || (MovingType = {}));
export var ActionType;
(function (ActionType) {
    ActionType[ActionType["All"] = 0] = "All";
    ActionType[ActionType["Click"] = 1] = "Click";
    ActionType[ActionType["DoubleClick"] = 2] = "DoubleClick";
})(ActionType || (ActionType = {}));
export class Movement {
    constructor() {
        /// <summary>
        /// Состояние когда тулза рисуется мышкой
        /// </summary>
        this.CreationState = true;
        /// <summary>
        /// Перемещается точка N тулзы
        /// </summary>
        this.MovingPointNo = -1;
        // чтобы не рассчитывать перемещение через векторы, что чревато погрешностью и потерями призводительности,
        // сделаю как в старой вебке, расчет на базе координат курсора
        this.PrevX = 0;
        this.PrevY = 0;
    }
}
// #endregion
export class ProMath {
}
// #region Const
ProMath.epsilon = 0.00000000001; // minimal price value
// +/- 1 млрд. для того чтоб int.MinValue/int.MaxValue влазили
ProMath.infinityMinus = -1 / 0.000000001; // minimal price value - !!!AlexB: now can be negative!!!
ProMath.infinity = 1 / 0.000000001; // maximal price value
ProMath.grFreeHand = 4; // свободное
ProMath.grOpen = 0; // открытие
ProMath.grClose = 1; // закрытие
ProMath.grBid = 1; // БИД (предложение)
ProMath.grAsk = 0; // АСК (спрос)
ProMath.grHi = 2; // максимальное
ProMath.grLow = 3; // минимальное
ProMath.grHL = 4; // среднее (макс+мин)/2
ProMath.grHLC = 5; // среднее2 (макс+мин+закр)/3
ProMath.grHLCC = 6; // среднее3 (макс+мин+2*закр)/4
ProMath.grZero = 7; // сервисная
ProMath.grOne = 8; // сервисная
ProMath.grCloseMinusOpen = 9; // сервисная
ProMath.grCloseMinusHi = 10; // сервисная
ProMath.grCloseMinusLow = 11; // сервисная
ProMath.grVolume = 12; // объемы
ProMath.grSimple = 0; // простой (скользящее среднее)
ProMath.grExponential = 1; // экспоненциальное сколязящее среднее
ProMath.grLinearWeighted = 2; // взвешенное
ProMath.grModified = 3; // модифицированное
